import { motion } from "framer-motion";

/* Auditor — nightly review digest preview.
   Simulates a "Tuesday 7:00 AM" digest with prioritized findings.
   The "critical" row pulses continuously via a Framer infinite `animate` loop —
   no React state is needed because the animation is driven by CSS-level transforms. */

/* Auditor — nightly review digest preview.
   Simulates a "Tuesday 7:00 AM" digest with prioritized findings. */

const DIGEST_ITEMS = [
    {
        id: "sol",
        severity: "critical",
        matter: "Wallace, K. · Wrongful death",
        finding: "Statute of limitations: 184 days remaining — no demand drafted",
        action: "Assigned: J. Marin · Due Friday",
        icon: "fa-hourglass-half",
    },
    {
        id: "ret",
        severity: "high",
        matter: "Patel, A. · Slip & fall",
        finding: "Retainer outstanding 11 days · 3 follow-ups sent · no response",
        action: "Suggested: paralegal call · script attached",
        icon: "fa-file-signature",
    },
    {
        id: "rec",
        severity: "medium",
        matter: "Hernandez, M. · Auto PI",
        finding: "ER records request open 38 days · Mercy Gen non-responsive",
        action: "Suggested: re-fax with priority cover sheet",
        icon: "fa-notes-medical",
    },
    {
        id: "off",
        severity: "medium",
        matter: "Cho v. Apex Logistics",
        finding: "Offer of $48K stale 9 days · client awaiting response",
        action: "Assigned: T. Diallo · respond today",
        icon: "fa-envelope-circle-check",
    },
    {
        id: "deadline",
        severity: "low",
        matter: "Tan family · Estate plan",
        finding: "Signing scheduled Apr 14 · trust funding checklist 70% complete",
        action: "Auto-nudge sent to client portal",
        icon: "fa-calendar-check",
    },
];

const SEVERITY = {
    critical: { label: "CRITICAL", bg: "rgba(220,38,38,0.10)", color: "#B91C1C", dot: "#DC2626" },
    high:     { label: "HIGH",     bg: "rgba(217,119,6,0.12)", color: "#B45309", dot: "#F59E0B" },
    medium:   { label: "MEDIUM",   bg: "rgba(30,91,255,0.10)", color: "#1E5BFF", dot: "#1E5BFF" },
    low:      { label: "LOW",      bg: "rgba(35,31,35,0.06)",  color: "#5C5159", dot: "#9B8F94" },
};

const AuditorShowcase = () => {
    return (
        <section data-testid="auditor-showcase" className="relative py-20 md:py-28 bg-[var(--cream-2)] overflow-hidden">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                {/* Digest preview */}
                <div className="lg:col-span-7 order-1">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.7 }}
                        className="card-paper overflow-hidden"
                        data-testid="auditor-digest"
                    >
                        {/* Mock email header */}
                        <div className="px-6 py-4 border-b border-[var(--border)] bg-[var(--paper)] flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center">
                                    <i className="fa-solid fa-clipboard-check text-[14px]" />
                                </div>
                                <div>
                                    <div className="font-display font-semibold text-[15px] text-[var(--ink)] leading-tight">Daily digest · Auditor</div>
                                    <div className="font-mono text-[10.5px] tracking-wider text-[var(--muted)] uppercase mt-0.5">Tuesday · 07:00 AM · 14 matters reviewed overnight</div>
                                </div>
                            </div>
                            <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-mono text-[9.5px] tracking-[0.14em] uppercase bg-[var(--blue-soft)] accent-blue font-semibold">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--blue)] opacity-70 animate-ping" />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--blue)]" />
                                </span>
                                Live
                            </span>
                        </div>

                        {/* Summary strip */}
                        <div className="px-6 py-4 bg-[var(--cream)] border-b border-[var(--border)] grid grid-cols-3 gap-4">
                            {[
                                { v: "5", l: "Findings", c: "var(--ink)" },
                                { v: "1", l: "Critical", c: "#B91C1C" },
                                { v: "3", l: "Auto-resolved", c: "#15803D" },
                            ].map((s) => (
                                <div key={s.l} className="text-left">
                                    <div className="font-display font-bold text-2xl leading-none" style={{ color: s.c }}>{s.v}</div>
                                    <div className="font-mono text-[10px] tracking-wider text-[var(--muted)] uppercase mt-1">{s.l}</div>
                                </div>
                            ))}
                        </div>

                        {/* Findings list */}
                        <ul className="divide-y divide-[var(--border)]">
                            {DIGEST_ITEMS.map((item, i) => {
                                const s = SEVERITY[item.severity];
                                const isUrgent = item.severity === "critical";
                                return (
                                    <motion.li
                                        key={item.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.45, delay: 0.05 * i }}
                                        className="px-6 py-4 hover:bg-[var(--cream)] transition-colors"
                                    >
                                        <div className="flex items-start gap-4">
                                            <motion.div
                                                animate={isUrgent ? { scale: [1, 1.08, 1] } : {}}
                                                transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 1.8 }}
                                                className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center"
                                                style={{ background: s.bg, color: s.color }}
                                                data-testid={`auditor-item-${item.id}`}
                                            >
                                                <i className={`fa-solid ${item.icon} text-sm`} />
                                            </motion.div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span
                                                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono text-[9px] tracking-[0.14em] uppercase font-semibold"
                                                        style={{ background: s.bg, color: s.color }}
                                                    >
                                                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
                                                        {s.label}
                                                    </span>
                                                    <span className="text-[13px] font-semibold text-[var(--ink)] truncate">{item.matter}</span>
                                                </div>
                                                <p className="text-[13px] text-[var(--ink-soft)] mt-1.5 leading-snug">{item.finding}</p>
                                                <div className="font-mono text-[10.5px] tracking-wider text-[var(--muted)] uppercase mt-2 flex items-center gap-1.5">
                                                    <i className="fa-solid fa-arrow-turn-down text-[9px]" />
                                                    {item.action}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.li>
                                );
                            })}
                        </ul>
                    </motion.div>
                </div>

                {/* Copy */}
                <div className="lg:col-span-5 order-2">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="eyebrow-pill">
                            <i className="fa-solid fa-clipboard-check text-[10px] accent-blue" />
                            Auditor · Nightly review
                        </span>
                        <h2 className="font-display text-[clamp(1.9rem,3.6vw,2.8rem)] leading-[1.05] tracking-tight font-semibold text-[var(--ink)] mt-5">
                            Every matter reviewed. <span className="accent-blue">Every morning.</span>
                        </h2>
                        <p className="mt-5 text-base md:text-lg text-[var(--ink-soft)] leading-relaxed">
                            While the firm sleeps, Auditor walks every open matter looking for slipping deadlines, missing records, stalled retainers, and stale offers — then hands your team a ranked work queue by 7 AM.
                        </p>
                        <ul className="mt-6 space-y-3 text-[14.5px] text-[var(--ink-soft)]">
                            {[
                                ["fa-bell", "Critical findings paged to the assigned attorney"],
                                ["fa-arrow-trend-up", "Auto-resolves 30-40% of mid-severity items overnight"],
                                ["fa-list-check", "Suggested action per finding — drafted, not just flagged"],
                                ["fa-bullseye", "Trained on YOUR firm's playbook, not a generic checklist"],
                            ].map(([icon, line]) => (
                                <li key={line} className="flex items-start gap-3">
                                    <span className="w-6 h-6 rounded-md bg-[var(--blue-soft)] accent-blue flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <i className={`fa-solid ${icon} text-[10px]`} />
                                    </span>
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default AuditorShowcase;
